
import React, { useState, useEffect } from 'react'
import { X, CreditCard } from 'lucide-react'

interface DadosBancariosFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  funcionario: any
}

export const DadosBancariosForm: React.FC<DadosBancariosFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  funcionario
}) => {
  const [formData, setFormData] = useState({
    tipoPagamento: 'transferencia' as 'transferencia' | 'pix',
    banco: '',
    agencia: '',
    conta: '',
    chavePix: '',
    tipoPix: 'cpf' as 'cpf' | 'email' | 'telefone' | 'chave_aleatoria'
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (funcionario?.dadosBancarios) {
      setFormData({
        tipoPagamento: funcionario.dadosBancarios.tipoPagamento || 'transferencia',
        banco: funcionario.dadosBancarios.banco || '',
        agencia: funcionario.dadosBancarios.agencia || '',
        conta: funcionario.dadosBancarios.conta || '',
        chavePix: funcionario.dadosBancarios.chavePix || '',
        tipoPix: funcionario.dadosBancarios.tipoPix || 'cpf'
      })
    }
  }, [funcionario, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await onSubmit({
        dadosBancarios: formData
      })
      onClose()
    } catch (error) {
      console.error('Erro ao salvar dados bancários:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !funcionario) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="border-b p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Dados Bancários</h2>
              <p className="text-gray-600">{funcionario.nome}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Pagamento
              </label>
              <select
                value={formData.tipoPagamento}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  tipoPagamento: e.target.value as 'transferencia' | 'pix'
                }))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="transferencia">Transferência Bancária</option>
                <option value="pix">PIX</option>
              </select>
            </div>

            {formData.tipoPagamento === 'transferencia' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banco *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.banco}
                    onChange={(e) => setFormData(prev => ({ ...prev, banco: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agência *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.agencia}
                    onChange={(e) => setFormData(prev => ({ ...prev, agencia: e.target.value }))}
                    placeholder="0000-0"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conta *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.conta}
                    onChange={(e) => setFormData(prev => ({ ...prev, conta: e.target.value }))}
                    placeholder="00000-0"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {formData.tipoPagamento === 'pix' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Chave PIX *
                  </label>
                  <select
                    required
                    value={formData.tipoPix}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      tipoPix: e.target.value as any
                    }))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="cpf">CPF</option>
                    <option value="email">E-mail</option>
                    <option value="telefone">Telefone</option>
                    <option value="chave_aleatoria">Chave Aleatória</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chave PIX *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.chavePix}
                    onChange={(e) => setFormData(prev => ({ ...prev, chavePix: e.target.value }))}
                    placeholder={
                      formData.tipoPix === 'cpf' ? '000.000.000-00' :
                      formData.tipoPix === 'email' ? 'email@exemplo.com' :
                      formData.tipoPix === 'telefone' ? '(11) 99999-9999' :
                      'Chave aleatória'
                    }
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <CreditCard size={16} />
              {loading ? 'Salvando...' : 'Salvar Dados Bancários'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DadosBancariosForm
